/**
 * HaulerCentral Auth Store
 *
 * Zustand store for HaulerCentral authentication state.
 * Following patterns from @commander/auth.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AUTH_STORAGE_KEYS } from '../constants/authConstants';
import * as authApi from '../services/authApi';
import type { AuthStore, HaulerCentralUser } from '../types/auth.types';

interface AuthStoreState extends AuthStore {
  // Internal state
  _hasHydrated: boolean;
  setHasHydrated: (hydrated: boolean) => void;
}

/**
 * Create auth store with Zustand
 */
export const useHaulerCentralAuthStore = create<AuthStoreState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      // Actions
      login: async (payload) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login(payload);

          if (response.success && response.data?.user) {
            set({
              user: response.data.user,
              isAuthenticated: !!response.data.accessToken,
              isLoading: false,
            });
          } else {
            throw new Error(response.message || 'Login failed');
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Login failed',
          });
          throw error;
        }
      },

      register: async (payload) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.register(payload);

          // Registration is successful if success=true, even if user/tokens aren't returned yet
          // (e.g., when email verification is required)
          if (response.success) {
            // Only set auth state if tokens were returned (instant login flow)
            if (response.data?.user && response.data?.accessToken) {
              set({
                user: response.data.user,
                isAuthenticated: true,
                isLoading: false,
              });
            } else {
              // Registration successful but requires email verification
              set({
                isLoading: false,
              });
            }
          } else {
            throw new Error(response.message || 'Registration failed');
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Registration failed',
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authApi.logout();
        } catch (error) {
          // Ignore logout errors
          console.error('Logout error:', error);
        } finally {
          // Clear state regardless of API call success
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      refreshToken: async () => {
        try {
          const refreshTokenValue = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
          if (!refreshTokenValue) {
            return false;
          }

          const response = await authApi.refreshToken(refreshTokenValue);

          if (response.success && response.data?.accessToken) {
            return true;
          }

          return false;
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Clear auth on refresh failure
          get().logout();
          return false;
        }
      },

      updateUser: (userData) => {
        const currentUser = get().user;
        if (currentUser) {
          const updatedUser = { ...currentUser, ...userData } as HaulerCentralUser;
          set({ user: updatedUser });

          // Update localStorage
          localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setHasHydrated: (hydrated) => {
        set({ _hasHydrated: hydrated });
      },

      setUser: (user: HaulerCentralUser | null) => {
        set({ user });
      },

      setIsAuthenticated: (isAuthenticated: boolean) => {
        set({ isAuthenticated });
      },
    }),
    {
      name: AUTH_STORAGE_KEYS.AUTH_STATE,
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        _hasHydrated: state._hasHydrated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

/**
 * Hook to check if store has been hydrated
 */
export function useHasHydrated(): boolean {
  return useHaulerCentralAuthStore((state) => state._hasHydrated);
}

export default useHaulerCentralAuthStore;
